import { Filmomtale } from '@filmweb/sanity-types';
import SArticleTitle from "components/framework/SArticleTitle";
import SMaxWidthContainer from 'components/framework/SMaxWidthContainer';
import SpecialShowsProgram from 'components/frontpage/specialshows/SpecialShowsProgram';
import { CMSCroppedImage, OgImage } from 'components/images/Image';
import RichText, { QUERY_FRAGMENT_BODYTEXT, QUERY_FRAGMENT_INGRESS } from 'components/richtext/RichText';
import { toRem } from "lib/client/fonts";
import { useDocumentTitle } from 'lib/client/useTitle';
import { setDefaultCacheHeaders } from 'lib/server/cache';
import { fwpakkeQuery, getFilmwebFilmomtale, getServerData } from 'lib/server/sanity';
import { DocumentType_SPECIALSHOW, FwPakkeImageDocument, ICommonProps, IMovieArticle, IQueryResult, ISpecialShowArticle } from 'lib/types/sanity';
import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import Head from 'next/head';
import styled from 'styled-components';


//#region [Other]


const PAGE_QUERY: string = `
{
	"published": *[_type =="${DocumentType_SPECIALSHOW}" && slug.current == $slug && !(_id in path("drafts.**"))] {
		...,
		${QUERY_FRAGMENT_INGRESS},
		${QUERY_FRAGMENT_BODYTEXT},
		image->,
		movies[]->
	}[0],
	"preview": *[_type =="${DocumentType_SPECIALSHOW}" && slug.current == $slug && _id in path("drafts.**")] {
		...,
		${QUERY_FRAGMENT_INGRESS},
		${QUERY_FRAGMENT_BODYTEXT},
		image->,
		movies[]->
	}[0]
}
`;
//#endregion

//#region [Other]getServerSideProps
export const getServerSideProps: GetServerSideProps = getServerData(async (context) => {
	const slug = context.params?.slug;
	if (!slug) {
		return { notFound: true };
	}

	setDefaultCacheHeaders(context as GetServerSidePropsContext);

	const pageData = await fwpakkeQuery<IQueryResult<ISpecialShowArticle>>(PAGE_QUERY, { slug });

	const ctx = context as GetServerSidePropsContext;
	const isP = ctx.query?.p === "1" && context.preview;
	const document = isP ? (pageData?.preview ?? pageData?.published) : pageData?.published;

	if (!document) {
		console.warn("Article not found", slug);
		return { notFound: true };
	}

	if (!isP && document.slug?.current !== slug) {
		console.warn("Article found, but slug does not match latest published slug", slug);
		return { notFound: true };
	}

	let filmwebMovies: Filmomtale[] | undefined;

	if ((document?.movies?.length ?? 0) > 0) {
		const edis = document!.movies?.filter(m => !!m)?.map(movie => (movie as IMovieArticle).edi.current);
		if ((edis?.length ?? 0) > 0) {
			filmwebMovies = await getFilmwebFilmomtale(edis);
		}

		// order the movies so they are in the same order as the article
		filmwebMovies?.sort((a, b) => edis.indexOf(a.mainVersionEDI) - edis.indexOf(b.mainVersionEDI));

	}

	return {
		props: {
			document,
			relatedMovies: filmwebMovies ?? null
		}
	};

});
//#endregion


//#region [Styles]
const SArticleIcon = styled.div`
	margin: 0 auto;
	max-width: 80%;
	position: relative;

	@media screen and (min-width: 641px ) {
		max-width: 25%;
		flex: 0 0 25%;
	}
`;

const SArticleImage = styled(CMSCroppedImage)`
	display: block;
	width: 100%;
	height: auto;
	border-radius: 50%;
	overflow: hidden;
`;

const STextContainer = styled(SMaxWidthContainer)`
	margin-bottom: 50px;
	padding-left: 10px;
	padding-right: 10px;
	overflow: hidden;

	display: flex;
	flex-direction: column;
	gap: 30px;

	@media screen and (min-width: 641px ){
		flex-direction: row;
	}

`;

const STextColumn = styled.div`
	flex: 1 1 auto;
`;

const SIngress = styled(RichText)`
	font-size: 1rem;
	margin: 50px 0;
	font-weight: var(--font_bold);
	margin: 0;

	@media screen and (min-width: 641px) {
		padding: 0;
		font-size: ${toRem(21)};
	}
`;

//#endregion


//#region [Props]
type NewsProps = {
	document: ISpecialShowArticle;
	relatedMovies?: Filmomtale[];
} & ICommonProps;
//#endregion

//#region [Component]
export default function Specialshow({ document, appData, relatedMovies }: NewsProps) {
	const title = useDocumentTitle(appData.settings, document);

	return (<SMaxWidthContainer as="article">
		<Head>
			<title>{title}</title>
			<meta property="og:title" content={title} key="title" />
		</Head>
		<OgImage document={document} />
		<SArticleTitle>{document.title}</SArticleTitle>

		<STextContainer $forContent={!document.image}>
			{document.image && <SArticleIcon>
				<SArticleImage image={document.image as FwPakkeImageDocument} width={355} heightRatio={1} altText={document.title} />
			</SArticleIcon>}
			<STextColumn>
				<SIngress value={document.ingress} />
				<RichText value={document.bodyText} />
			</STextColumn>
		</STextContainer>

		<SpecialShowsProgram settings={appData.settings} showType={document.showType} ediIncludeOnly={relatedMovies?.map(f => f.mainVersionEDI)} />

	</SMaxWidthContainer>);
}
//#endregion
